import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface MarketingStatus {
  status: {
    twilio_sms: { configured: boolean; from_number?: string | null; whatsapp_from?: string | null; verify_sid?: string | null; whatsapp_ready?: boolean; verify_ready?: boolean };
    meta_wa:    { configured: boolean; phone_number_id?: string | null };
    google_ads: { configured: boolean; customer_id?: string | null };
    meta_ads:   { configured: boolean; ad_account_id?: string | null };
  };
  updated_at?: string;
}

/**
 * Marketing providers card — one panel per platform (Twilio, Meta WhatsApp,
 * Google Ads, Meta Ads). Blanks are ignored server-side so admins can rotate
 * one credential at a time without wiping other secrets.
 */
export const MarketingProvidersCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<MarketingStatus>({
    queryKey: ["marketing-providers"],
    queryFn: async () => (await api.get("/admin/marketing-providers")).data,
  });
  const s = q.data?.status;

  // Local form drafts — we only PUT non-empty values so admins never wipe secrets by accident.
  const [twilio,    setTwilio]    = React.useState({ account_sid: "", auth_token: "", from_number: "", whatsapp_from: "", verify_sid: "" });
  const [metaWa,    setMetaWa]    = React.useState({ access_token: "", phone_number_id: "", business_account_id: "" });
  const [googleAds, setGoogleAds] = React.useState({ developer_token: "", refresh_token: "", customer_id: "" });
  const [metaAds,   setMetaAds]   = React.useState({ access_token: "", ad_account_id: "" });

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/marketing-providers", {
      twilio_sms: twilio,   meta_wa: metaWa,
      google_ads: googleAds, meta_ads: metaAds,
    })).data,
    onSuccess: () => {
      toast.success("Marketing providers saved.");
      setTwilio({ account_sid: "", auth_token: "", from_number: "", whatsapp_from: "", verify_sid: "" });
      setMetaWa({ access_token: "", phone_number_id: "", business_account_id: "" });
      setGoogleAds({ developer_token: "", refresh_token: "", customer_id: "" });
      setMetaAds({ access_token: "", ad_account_id: "" });
      qc.invalidateQueries({ queryKey: ["marketing-providers"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Save failed"),
  });

  const Pill = ({ ok, label }: { ok?: boolean; label: string }) => (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      ok ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
         : "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-300"}`}>
      {ok ? "LIVE" : "MOCK"} · {label}
    </span>
  );

  return (
    <div className="sg-card p-6 mt-6" data-testid="marketing-providers-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div>
          <div className="sg-label">Marketing providers</div>
          <p className="text-sm text-[color:var(--sg-muted)] max-w-2xl mt-0.5">
            Paste real API keys to flip WhatsApp / SMS / Ads pages from mock → live send. Blanks are ignored so you can safely rotate one credential at a time.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Pill ok={s?.twilio_sms.configured} label="Twilio (SMS/WA/OTP)"/>
          <Pill ok={s?.meta_wa.configured}    label="Meta WhatsApp"/>
          <Pill ok={s?.google_ads.configured} label="Google Ads"/>
          <Pill ok={s?.meta_ads.configured}   label="Meta Ads"/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ProviderBlock title="Twilio (SMS · WhatsApp · Verify)" href="https://console.twilio.com/">
          <Field label="Account SID"        testId="mp-twilio-sid"       value={twilio.account_sid}   onChange={(v) => setTwilio({ ...twilio, account_sid: v })}   placeholder={s?.twilio_sms.configured ? "•••••" : "AC…"}/>
          <Field label="Auth Token"         testId="mp-twilio-token"     value={twilio.auth_token}    onChange={(v) => setTwilio({ ...twilio, auth_token: v })}    placeholder={s?.twilio_sms.configured ? "•••••" : "…"} secret/>
          <Field label="SMS From number"    testId="mp-twilio-from"      value={twilio.from_number}   onChange={(v) => setTwilio({ ...twilio, from_number: v })}   placeholder={s?.twilio_sms.from_number || "+1XXXXXXXXXX"}/>
          <Field label="WhatsApp From"      testId="mp-twilio-wa-from"   value={twilio.whatsapp_from} onChange={(v) => setTwilio({ ...twilio, whatsapp_from: v })} placeholder={s?.twilio_sms.whatsapp_from || "whatsapp:+14155238886"}/>
          <Field label="Verify Service SID" testId="mp-twilio-verify"    value={twilio.verify_sid}    onChange={(v) => setTwilio({ ...twilio, verify_sid: v })}    placeholder={s?.twilio_sms.verify_sid ? "•••••" : "VA…"}/>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s?.twilio_sms.configured ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-300"}`}>SMS · {s?.twilio_sms.configured && s?.twilio_sms.from_number ? "LIVE" : "OFF"}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s?.twilio_sms.whatsapp_ready ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-300"}`}>WA · {s?.twilio_sms.whatsapp_ready ? "LIVE" : "OFF"}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s?.twilio_sms.verify_ready ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-300"}`}>OTP · {s?.twilio_sms.verify_ready ? "LIVE" : "OFF"}</span>
          </div>
          <details className="mt-3 text-xs text-[color:var(--sg-muted)]">
            <summary className="cursor-pointer font-semibold text-[color:var(--sg-fg)]">How to get these keys →</summary>
            <ol className="list-decimal ml-5 mt-2 space-y-1 leading-relaxed">
              <li>Create a free account at <a className="underline" href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer">twilio.com/try-twilio</a>.</li>
              <li>Copy <strong>Account SID</strong> + <strong>Auth Token</strong> from the <a className="underline" href="https://console.twilio.com" target="_blank" rel="noreferrer">Twilio Console</a> dashboard.</li>
              <li>Buy a phone number under <em>Phone Numbers → Buy a number</em> and paste it as <strong>SMS From number</strong> in E.164 (e.g. <code>+14155551234</code>).</li>
              <li>For WhatsApp: enable the <a className="underline" href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noreferrer">WhatsApp sandbox</a> (fastest) or add a live sender, then paste as <strong>whatsapp:+1…</strong>.</li>
              <li>For OTP: create a service at <a className="underline" href="https://console.twilio.com/us1/develop/verify/services" target="_blank" rel="noreferrer">Verify → Services</a>, copy the <strong>Service SID</strong> (starts with <code>VA</code>).</li>
            </ol>
          </details>
          <TwilioTestBox configured={!!s?.twilio_sms.configured} whatsappReady={!!s?.twilio_sms.whatsapp_ready}/>
        </ProviderBlock>

        <ProviderBlock title="Meta WhatsApp Business" href="https://developers.facebook.com/apps">
          <Field label="Access token"        testId="mp-wa-token"    value={metaWa.access_token}        onChange={(v) => setMetaWa({ ...metaWa, access_token: v })}        placeholder={s?.meta_wa.configured ? "•••••" : "EAAG…"} secret/>
          <Field label="Phone number ID"     testId="mp-wa-phone"    value={metaWa.phone_number_id}     onChange={(v) => setMetaWa({ ...metaWa, phone_number_id: v })}     placeholder="1234567890"/>
          <Field label="Business account ID" testId="mp-wa-business" value={metaWa.business_account_id} onChange={(v) => setMetaWa({ ...metaWa, business_account_id: v })} placeholder="1234567890"/>
        </ProviderBlock>

        <ProviderBlock title="Google Ads" href="https://ads.google.com/aw/apicenter">
          <Field label="Developer token" testId="mp-gads-dev"     value={googleAds.developer_token} onChange={(v) => setGoogleAds({ ...googleAds, developer_token: v })} placeholder={s?.google_ads.configured ? "•••••" : "abc123…"} secret/>
          <Field label="Refresh token"   testId="mp-gads-refresh" value={googleAds.refresh_token}   onChange={(v) => setGoogleAds({ ...googleAds, refresh_token: v })}   placeholder="1//0…" secret/>
          <Field label="Customer ID"     testId="mp-gads-cust"    value={googleAds.customer_id}     onChange={(v) => setGoogleAds({ ...googleAds, customer_id: v })}     placeholder="1234567890"/>
        </ProviderBlock>

        <ProviderBlock title="Meta Ads" href="https://business.facebook.com/settings">
          <Field label="Access token"   testId="mp-mads-token" value={metaAds.access_token}  onChange={(v) => setMetaAds({ ...metaAds, access_token: v })}  placeholder={s?.meta_ads.configured ? "•••••" : "EAAG…"} secret/>
          <Field label="Ad account ID"  testId="mp-mads-acct"  value={metaAds.ad_account_id} onChange={(v) => setMetaAds({ ...metaAds, ad_account_id: v })} placeholder="act_1234567890"/>
        </ProviderBlock>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="text-[11px] text-[color:var(--sg-muted)]">
          {q.data?.updated_at ? `Last updated ${new Date(q.data.updated_at).toLocaleString()}` : "Never configured"}
        </div>
        <button
          data-testid="mp-save"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="h-10 px-5 rounded-md bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold text-sm"
        >
          {save.isPending ? "Saving…" : "Save marketing providers"}
        </button>
      </div>
    </div>
  );
};

const ProviderBlock: React.FC<{ title: string; href: string; children: React.ReactNode }> = ({ title, href, children }) => (
  <div className="rounded-xl border border-[color:var(--sg-border)] p-4">
    <div className="flex items-center justify-between mb-2">
      <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{title}</div>
      <a href={href} target="_blank" rel="noreferrer" className="text-[10px] text-[color:var(--sb-saffron,#F97316)] hover:underline">Console →</a>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string; secret?: boolean; testId?: string }> = ({ label, value, onChange, placeholder, secret, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">{label}</div>
    <input
      data-testid={testId}
      type={secret ? "password" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 h-9 w-full rounded-md border border-[color:var(--sg-border)] bg-transparent px-2 text-sm"
      autoComplete="off"
    />
  </div>
);

const TwilioTestBox: React.FC<{ configured: boolean; whatsappReady: boolean }> = ({ configured, whatsappReady }) => {
  const [to, setTo] = React.useState("");
  const [channel, setChannel] = React.useState<"sms" | "whatsapp">("sms");
  const [result, setResult] = React.useState<{ ok: boolean; text: string } | null>(null);
  const canSend = channel === "sms" ? configured : whatsappReady;

  const formatErr = (e: any): string => {
    const d = e?.response?.data?.detail ?? e?.message ?? "Test failed";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((it) => it?.msg || JSON.stringify(it)).join(" · ");
    return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
  };

  const test = useMutation({
    mutationFn: async () => (await api.post("/twilio/test", { channel, to: to.trim() })).data,
    onSuccess: (d) => {
      if (d?.ok) {
        setResult({ ok: true, text: `Sent · SID ${(d.sid || "").slice(0, 12)}… · status ${d.status || "queued"}` });
        toast.success("Test message delivered to Twilio.");
      } else {
        const msg = typeof d?.error === "string" ? d.error : "Twilio rejected the request.";
        setResult({ ok: false, text: msg });
        toast.error(msg);
      }
    },
    onError: (e: any) => {
      const msg = formatErr(e);
      setResult({ ok: false, text: msg });
      toast.error(msg);
    },
  });

  return (
    <div className="mt-3 rounded-lg border border-dashed border-[color:var(--sb-saffron,#F97316)]/40 bg-[color:var(--sb-saffron,#F97316)]/5 p-3" data-testid="twilio-test-box">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-[color:var(--sg-fg)]">Send a test message</div>
        <div className="inline-flex rounded-full border border-[color:var(--sg-border)] p-0.5 bg-[color:var(--sg-card)]">
          {(["sms", "whatsapp"] as const).map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`tw-test-ch-${c}`}
              onClick={() => setChannel(c)}
              className={`px-2.5 py-0.5 text-[11px] font-semibold rounded-full transition ${channel === c ? "bg-[color:var(--sb-saffron,#F97316)] text-white" : "text-[color:var(--sg-muted)]"}`}
            >{c === "sms" ? "SMS" : "WhatsApp"}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <input
          data-testid="tw-test-to"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="+14155551234  (E.164, must be verified on Twilio trial)"
          className="flex-1 h-9 rounded-md border border-[color:var(--sg-border)] bg-transparent px-2 text-sm font-mono"
        />
        <Button
          data-testid="tw-test-send"
          onClick={() => { setResult(null); test.mutate(); }}
          disabled={!canSend || !to.trim() || test.isPending}
          className="h-9 bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold text-xs px-3"
        >
          {test.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Send test"}
        </Button>
      </div>
      {!canSend && (
        <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
          {channel === "sms" ? "Save Account SID + Auth Token + SMS From number first." : "Save WhatsApp From (whatsapp:+…) first."}
        </div>
      )}
      {result && (
        <div className={`mt-2 flex items-start gap-1.5 text-[11px] ${result.ok ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`} data-testid="tw-test-result">
          {result.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0"/> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/>}
          <span>{result.text}</span>
        </div>
      )}
      <div className="mt-2 text-[10px] text-[color:var(--sg-muted)]">
        Trial accounts can only send to <a className="underline" href="https://console.twilio.com/us1/develop/phone-numbers/manage/verified" target="_blank" rel="noreferrer">verified caller IDs</a>. Upgrade to send to any number.
      </div>
    </div>
  );
};
